import React from "react";
import { Helmet } from "react-helmet-async";
import { BIZ } from "@/lib/content";
import { useSEOOverride } from "@/lib/seoOverrides";
import { getPageSEO, DEFAULT_KEYWORDS } from "@/lib/pageSEO";

/**
 * Centralised SEO component.
 * Priority for each field (highest → lowest):
 *   1. Live override from /admin/seo (MongoDB) — runtime CMS
 *   2. Explicit props passed by the page
 *   3. Central PAGE_SEO map (contractor-agreed exact strings, keyed by path)
 *   4. Hard-coded sensible default
 *
 * Always emits: title, description, canonical, og:type/title/description/url/image/site_name,
 * twitter card/title/description/image, meta keywords, optional jsonLd graph,
 * optional noindex robots directive.
 *
 * NOTE (React 19 + react-helmet-async): the app's /public/index.html deliberately
 * does NOT ship static <meta name="description">, <link rel="canonical">, OG or
 * Twitter tags. Under React 19 those are auto-hoisted from JSX into <head>, and
 * having static duplicates in the source HTML creates unresolvable double tags.
 * Production per-route HTML files are patched by
 * /app/frontend/scripts/prerender-metadata.cjs so non-JS crawlers still see
 * route-specific SEO tags on first byte.
 */

const DEFAULT_OG_IMAGE = `${BIZ.url}/og-image.png`;

export default function SEO({
  title,
  description,
  keywords,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const override = useSEOOverride(path);
  const central = getPageSEO(path);

  const codeTitle = title || central?.title || "Functional Capacity Assessment Australia | FCA Reports Australia";
  const codeDesc =
    description ||
    central?.description ||
    "Fast, virtual NDIS Functional Capacity Assessments by experienced AHPRA-registered Occupational Therapists. Report delivered within five business days of the final assessment interview.";

  const finalTitle = override.meta_title || codeTitle;
  const finalDesc = override.meta_description || codeDesc;

  const keywordsArr =
    keywords ||
    central?.keywords ||
    DEFAULT_KEYWORDS;
  const keywordsStr = Array.isArray(keywordsArr) ? keywordsArr.join(", ") : String(keywordsArr || "");

  const url = `${BIZ.url}${path}`;
  const finalImage = image || DEFAULT_OG_IMAGE;

  // JSON-LD can be a single object or an array of objects
  const jsonLdNodes = jsonLd
    ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {keywordsStr ? <meta name="keywords" content={keywordsStr} /> : null}
      {noindex && <meta name="robots" content="noindex, follow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
      {!noindex && <link rel="canonical" href={url} />}
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="FCA Reports Australia" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />
      {jsonLdNodes.map((node, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
}
